// Carga del carrito de una mesa al seleccionarla (TPV grande y móvil).
//
// Un solo sitio para "dejar el carrito con lo de esta mesa": se usa al
// pulsar una mesa (ProductItem), al terminar de forzar una mesa que se
// estaba cobrando en otro dispositivo, al cancelar ese forzado (volver a la
// mesa anterior) y al recargar la página con una mesa seleccionada.
//
//   1) Limpia el carrito (y el modo borrar) y marca la mesa activa.
//   2) Recoloca el borrador local de la mesa (líneas sin guardar ni comandar).
//   3) Si la mesa tiene sesión, pide su checkout al servidor y lo hidrata.

import axios from "src/utils/axios";
import {
  addCartMany,
  resetCart,
  resetCheckoutToDelete,
  setDeleteMode,
  syncCartCheckoutComplete,
} from "src/redux/slices/product";
import { getSessionSuccess } from "src/redux/slices/session";
import { getOrdersSuccess } from "src/redux/slices/order";
import { getMesaSuccess, updateMesaSession } from "src/redux/slices/mesas";
import { setActiveMesa, setActiveSession } from "src/redux/slices/active";
import { markLocalCheckoutFetch } from "src/utils/localCheckoutFetchTracker";

const EMPTY_SESSION = "00000000-0000-0000-0000-000000000000";

export const hasOpenSession = (mesa) => !!mesa?.sessionId && mesa.sessionId !== EMPTY_SESSION;

/** Líneas del borrador local de la mesa, listas para `addCartMany`. */
export function draftLinesForMesa(checkoutHistory, mesaId) {
  const draft = (checkoutHistory || []).find((u) => u.idMesa === mesaId);
  if (!draft?.checkout?.cart?.length) return [];
  return draft.checkout.cart.map((product) => ({
    ...product,
    quantity: product.quantity,
    subtotal: product.precio * product.quantity,
    notas: product.notas,
    complementos: product.complementos,
    servido: product.servido,
    old: product.old,
  }));
}

/** Recoloca en el carrito el borrador local de la mesa (si lo hay). */
export function restoreMesaDraft(dispatch, getState, mesaId) {
  const lines = draftLinesForMesa(getState()?.product?.checkoutHistory, mesaId);
  if (lines.length) dispatch(addCartMany(lines));
  return lines.length;
}

/** Pasos 1 y 2: carrito limpio con el borrador de la mesa. */
export function resetCartForMesa(dispatch, getState, mesaId) {
  dispatch(resetCart());
  dispatch(resetCheckoutToDelete());
  dispatch(setDeleteMode(false));
  dispatch(setActiveMesa(mesaId));
  return restoreMesaDraft(dispatch, getState, mesaId);
}

/** Pedidos de la mesa por la vía clásica (respaldo si falla getCheckout). */
export function fetchMesaAndOrders(dispatch, mesa) {
  return axios
    .get(`pedidos/getMesaAndOrders/${mesa?.idRestaurante}/${mesa?.id}`)
    .then((res) => {
      dispatch(getMesaSuccess(res.data.mesa));
      dispatch(getOrdersSuccess(res.data.orders));
    });
}

/**
 * Pedidos de la mesa tras comandar desde este dispositivo. El eco
 * `session.changed`/`pedido.*` de la propia comanda se descarta (eventRouter)
 * y la respuesta del POST no trae los pedidos, así que sin esto
 * `state.order.orders` se quedaba con las rondas anteriores y el
 * PaymentDialog (que solo refetchea si no hay pedidos de la mesa) cobraba
 * de menos. Solo toca `state.order.orders` (no el carrito).
 */
export function refreshMesaOrders(dispatch, idRestaurante, idMesa) {
  if (!idRestaurante || !idMesa) return Promise.resolve();
  return axios
    .get(`pedidos/getMesaAndOrders/${idRestaurante}/${idMesa}`)
    .then((res) => {
      if (Array.isArray(res?.data?.orders)) dispatch(getOrdersSuccess(res.data.orders));
    })
    .catch(() => {});
}

/**
 * Paso 3: checkout del servidor. `force` lo pide aunque la mesa (en la
 * lista local) no traiga sesión — tras recargar o forzar puede estar
 * desactualizada. Devuelve la sesión abierta, `null` si la mesa no tiene
 * sesión abierta (o está cerrada) o `undefined` si la petición falló.
 */
export async function fetchMesaCheckout(dispatch, mesa, { force = false } = {}) {
  const res = await requestMesaCheckout(mesa, { force });
  return applyMesaCheckout(dispatch, mesa, res);
}

/**
 * Solo la petición (sin tocar el store). `{ skipped }` si no hay que pedirla,
 * `{ response }` o `{ error }`.
 */
export async function requestMesaCheckout(mesa, { force = false } = {}) {
  if (!mesa?.id) return { skipped: true };
  if (!force && !hasOpenSession(mesa)) return { skipped: true };
  // El eco SignalR `mesa/{userId}` del self-heal del backend lo provoca
  // este mismo dispositivo: marcarlo evita un segundo getCheckout.
  markLocalCheckoutFetch(mesa.id);
  try {
    const response = await axios.get(`data/getCheckout/${mesa.id}/${mesa.idRestaurante}`);
    return { response };
  } catch (error) {
    return { error };
  }
}

/**
 * Hidrata el store con la respuesta de `requestMesaCheckout`. Devuelve la
 * sesión abierta, `null` (sin sesión abierta) o `undefined` (falló).
 */
export function applyMesaCheckout(dispatch, mesa, res) {
  if (!res || res.skipped) return null;
  if (res.error) {
    // Respaldo: openSessionFlow se salta getMesaAndOrders confiando en este
    // getCheckout; si falla, lo pedimos aquí.
    fetchMesaAndOrders(dispatch, mesa).catch(() => {});
    return undefined;
  }
  const { response } = res;
  const session = response?.data?.session;
  if (session == null || session.isClosed) return null;
  dispatch(setActiveSession(session.id));
  // Hidratar state.order.orders en el mismo tick que el carrito (sin esto
  // el PaymentDialog mostraba productos "sin comensal" un instante).
  if (Array.isArray(session.pedidos)) dispatch(getOrdersSuccess(session.pedidos));
  dispatch(syncCartCheckoutComplete(response.data.checkout));
  dispatch(
    updateMesaSession({
      mesa: session.idMesa,
      session: session.id,
      cuenta: response.data.checkout.subtotal,
    }),
  );
  dispatch(getSessionSuccess(session));
  return session;
}

/** Selección completa: carrito limpio + borrador + checkout del servidor. */
export function loadMesaCheckout(dispatch, getState, mesa, { force = false } = {}) {
  if (!mesa?.id) return Promise.resolve(null);
  // Invalida una apertura en curso (no debe pisar esta carga al resolver).
  openSeq += 1;
  blockedMesaId = null;
  heldCheckout = null;
  resetCartForMesa(dispatch, getState, mesa.id);
  return fetchMesaCheckout(dispatch, mesa, { force });
}

// Mesa cuya cuenta NO se pintó porque otro dispositivo la estaba cobrando
// (409 LEASE_CHARGING): al forzar hay que cargarla entera por primera vez.
let blockedMesaId = null;
let openSeq = 0;

// ── Cuenta pedida con el click pero no pintada (2026-10-07) ─────────
// Con «otro dispositivo cobrando» el getCheckout del click (en paralelo con
// el lease, para que el caso normal no sea más lento) se guarda AQUÍ, fuera
// del store, junto a una «marca de cambios» de la mesa. Al forzar se pinta
// sin volver a pedirla si la mesa no ha cambiado desde entonces; si cambió
// (evento en tiempo real de la mesa, resync/reconexión, ficha distinta en el
// plano o más de HELD_TTL_MS) se pide UNA vez. Antes: un getCheckout en el
// click y otro al forzar, siempre.
export const HELD_TTL_MS = 60_000;
let heldCheckout = null; // { mesaId, res, at, mark }
const mesaChangeCount = new Map(); // mesaId → nº de eventos vistos de la mesa
let changeEpoch = 0; // sube con cada resync/reconexión (invalida todo)

/** Un evento en tiempo real (de quien sea) tocó la mesa. */
export function noteMesaChanged(mesaId) {
  if (mesaId == null) return;
  const key = String(mesaId);
  mesaChangeCount.set(key, (mesaChangeCount.get(key) || 0) + 1);
}

/** Resync / reconexión / vuelta de la pestaña: ninguna cuenta guardada vale. */
export function invalidateHeldCheckouts() {
  changeEpoch += 1;
}

export function discardHeldCheckout() {
  heldCheckout = null;
}

// Lo que el plano sabe de la mesa (lo parchean los eventos, también los del
// hub legacy): si cambia, la cuenta guardada ya no es la de ahora.
function mesaSnapshot(getState, mesaId) {
  const m = (getState?.()?.mesas?.mesas || []).find((x) => String(x?.id) === String(mesaId));
  if (!m) return "";
  return [m.cuenta, m.sessionId, m.ocupada, m.comensales].join("|");
}

function changeMark(getState, mesaId) {
  return {
    n: mesaChangeCount.get(String(mesaId)) || 0,
    epoch: changeEpoch,
    snap: mesaSnapshot(getState, mesaId),
  };
}

function holdCheckout(getState, mesaId, res) {
  // Solo una respuesta buena sirve (sin pedir / error → al forzar se pide).
  heldCheckout = res?.response ? { mesaId, res, at: Date.now(), mark: changeMark(getState, mesaId) } : null;
}

/**
 * La cuenta guardada de `mesaId` si sigue valiendo (misma mesa, sin cambios
 * desde que se pidió y dentro del TTL); `null` si hay que pedirla. Siempre la
 * consume: se usa una vez.
 */
export function takeHeldCheckout(mesaId, getState, now = Date.now()) {
  const h = heldCheckout;
  heldCheckout = null;
  if (!h || String(h.mesaId) !== String(mesaId)) return null;
  if (now - h.at > HELD_TTL_MS) return null;
  const mark = changeMark(getState, mesaId);
  if (mark.n !== h.mark.n || mark.epoch !== h.mark.epoch || mark.snap !== h.mark.snap) return null;
  return h.res;
}

export const isMesaCheckoutBlocked = (mesaId) =>
  blockedMesaId != null && String(blockedMesaId) === String(mesaId);

/**
 * Pulsar una mesa (2026-10-07): la cuenta solo se pinta si la mesa es
 * nuestra. Se piden a la vez el lease (`waitLease`, el bridge lo adquiere) y
 * el checkout, pero el carrito no se llena hasta saber el resultado del
 * lease: con «otro dispositivo cobrando» la cuenta no aparece nunca (solo el
 * aviso «¿Forzar?»); al forzar la carga `loadMesaCheckout`.
 * El carrito queda vacío (sin el borrador) mientras tanto: nada de la mesa
 * se ve antes de saber si es nuestra.
 */
export async function openMesaCheckout(dispatch, getState, mesa, { waitLease, force = false } = {}) {
  if (!mesa?.id) return null;
  const seq = ++openSeq;
  blockedMesaId = null;
  heldCheckout = null;
  dispatch(resetCart());
  dispatch(resetCheckoutToDelete());
  dispatch(setDeleteMode(false));
  dispatch(setActiveMesa(mesa.id));
  const [lease, res] = await Promise.all([
    waitLease ? waitLease(mesa.id) : Promise.resolve({ ok: true }),
    requestMesaCheckout(mesa, { force }),
  ]);
  // Otra mesa abierta/pulsada mientras tanto: no tocar nada.
  if (seq !== openSeq) return null;
  // La selección manda (no `active.activeMesa`): al montar el TPV tras
  // recargar, tpv/index pone activeMesa a null a la vez que se restaura la
  // mesa y antes esta apertura se abandonaba → mesa seleccionada sin cuenta.
  const sel = getState()?.mesas?.selectedId;
  const selValid = sel != null && sel !== -1 && sel !== "-1";
  if (selValid && String(sel) !== String(mesa.id)) return null;
  if (lease?.charging) {
    blockedMesaId = mesa.id;
    // No se pinta, pero se guarda para no volver a pedirla al forzar.
    holdCheckout(getState, mesa.id, res);
    return null;
  }
  if (String(getState()?.active?.activeMesa) !== String(mesa.id)) dispatch(setActiveMesa(mesa.id));
  restoreMesaDraft(dispatch, getState, mesa.id);
  return applyMesaCheckout(dispatch, mesa, res);
}

/**
 * Recarga la cuenta de la mesa que YA se está viendo sin vaciar el carrito
 * (forzar una mesa que se cobraba en otro dispositivo). Antes se hacía la
 * selección completa: `resetCart` dejaba el carrito a 0 y el getCheckout lo
 * volvía a llenar → parpadeo de una cuenta que ya estaba leída.
 * `syncCartCheckoutComplete` sustituye las líneas enviadas por las del
 * servidor y conserva delante las locales sin enviar: sin cambios en el
 * servidor no cambia nada en pantalla.
 * Si el carrito es de otra mesa (la carga del primer click no llegó a
 * hacerse), selección completa. Si el servidor dice que la mesa ya no tiene
 * sesión abierta, queda solo su borrador (como en la selección completa).
 */
export async function refreshMesaCheckoutInPlace(dispatch, getState, mesa) {
  if (!mesa?.id) return null;
  const activeMesa = getState()?.active?.activeMesa;
  // Cuenta que nunca se pintó (bloqueada por «cobrando»): se pinta por
  // primera vez. Si la del click sigue valiendo, sin volver a pedirla.
  if (isMesaCheckoutBlocked(mesa.id)) {
    const held = takeHeldCheckout(mesa.id, getState);
    if (held) {
      openSeq += 1;
      blockedMesaId = null;
      resetCartForMesa(dispatch, getState, mesa.id);
      return applyMesaCheckout(dispatch, mesa, held);
    }
    blockedMesaId = null;
    return loadMesaCheckout(dispatch, getState, mesa, { force: true });
  }
  if (activeMesa == null || String(activeMesa) !== String(mesa.id)) {
    return loadMesaCheckout(dispatch, getState, mesa, { force: true });
  }
  // Nada pintado todavía (apertura interrumpida, carrito vaciado): carga
  // completa — no hay cuenta visible que pueda parpadear.
  if (!(getState()?.product?.checkout?.cart || []).length) {
    return loadMesaCheckout(dispatch, getState, mesa, { force: true });
  }
  const session = await fetchMesaCheckout(dispatch, mesa, { force: true });
  if (session === null) resetCartForMesa(dispatch, getState, mesa.id);
  return session ?? null;
}
